import { plainToInstance } from 'class-transformer'
import { Polygon } from 'geojson'
import center from '@turf/center'
import { polygon as turfPolygon } from '@turf/helpers'
import { IProjectLocationNodeBoundary } from './IProjectLocationNode'

export type InitialViewState = {
  latitude: number
  longitude: number
  zoom: number
}

/**
 * Represents a location node boundary -- a geographic polygon that defines the
 * spatial extent of a location in the hierarchy.
 *
 * Wraps the GeoJSON FeatureCollection returned by the API and provides helpers
 * to extract the polygon geometry, the admin-configured zoom level, and a
 * ready-to-use `initialViewState` for centering Mapbox maps.
 */
export class ProjectLocationNodeBoundary {
  type: 'FeatureCollection'
  features: IProjectLocationNodeBoundary['features']

  public static new(payload: unknown): ProjectLocationNodeBoundary | null {
    if (!payload) return null
    return plainToInstance(ProjectLocationNodeBoundary, payload)
  }

  /** Returns the polygon geometry from the first feature, or null. */
  public getGeometry(): Polygon | null {
    return this.features?.[0]?.geometry ?? null
  }

  /** Returns the admin-configured zoom level, or undefined if not set. */
  public getZoomLevel(): number | undefined {
    return this.features?.[0]?.properties?.zoomLevel
  }

  /**
   * Computes the center of the boundary polygon using @turf/center and pairs
   * it with the stored zoom level.
   *
   * Returns a view state object compatible with:
   * - Web: react-map-gl `initialViewState` prop  ({ latitude, longitude, zoom })
   * - Mobile: can be mapped to `cameraProps` ({ centerCoordinate, zoomLevel })
   *
   * Returns null if the boundary has no valid geometry.
   */
  public toInitialViewState(): InitialViewState | null {
    const geometry = this.getGeometry()
    if (!geometry) return null

    try {
      const centerPoint = center(turfPolygon(geometry.coordinates))

      return {
        longitude: centerPoint.geometry.coordinates[0],
        latitude: centerPoint.geometry.coordinates[1],
        zoom: this.getZoomLevel() ?? 16,
      }
    } catch {
      return null
    }
  }
}
